import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import { Shield, TrendingUp, Clock, Users, Package, CheckCircle } from 'lucide-react';
import { useSanityContent } from '../hooks/useSanityContent';
import { fetchAboutPage } from '../lib/sanity';
import { defaultAboutPage } from '../lib/defaultContent';
import type { AboutPageContent, AboutCertification } from '../lib/types';
import GrainBackground from '../components/GrainBackground';
import { useGrainAnimation } from '../hooks/useGrainAnimation';

const CertificationsSection: React.FC<{
  title: string;
  description: string;
  certs: AboutCertification[];
}> = ({ title, description, certs }) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <div id="certifications" className="mb-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-10"
      >
        <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
          {title}
        </h2>
        <p className="text-lg text-gray-700 max-w-3xl mx-auto">
          {description}
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6">
        {certs.map((cert, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="text-lg font-playfair font-bold text-primary-black mb-2">{cert.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{cert.body}</p>
              <button
                onClick={() => setExpanded(expanded === index ? null : index)}
                className="text-sm font-inter font-medium text-primary-red hover:text-red-700 transition-colors flex items-center gap-1"
              >
                {expanded === index ? 'Hide certificate' : 'See certificate'}
                <span className="text-xs">{expanded === index ? '▲' : '▼'}</span>
              </button>
            </div>
            {expanded === index && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="border-t border-gray-100"
              >
                <img
                  src={cert.image}
                  alt={`${cert.title} certificate`}
                  className="w-full object-cover"
                  style={{ maxHeight: '220px' }}
                />
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const About: React.FC = () => {
  const { grainAnimationEnabled } = useGrainAnimation();
  const { content } = useSanityContent<AboutPageContent>(fetchAboutPage, defaultAboutPage);
  const iconMap = { Shield, TrendingUp, Clock, Users, Package, CheckCircle };
  const qualityImages =
    content.qualityImages && content.qualityImages.length >= 3
      ? content.qualityImages
      : defaultAboutPage.qualityImages;
  const certifications =
    content.certifications && content.certifications.length > 0
      ? content.certifications
      : defaultAboutPage.certifications;
  const storyMediaUrl = content.storyMediaUrl || defaultAboutPage.storyMediaUrl;
  const relationshipsImage =
    content.relationshipsImage || defaultAboutPage.relationshipsImage;

  return (
    <main className="relative pt-20">
      {grainAnimationEnabled && <GrainBackground />}
      <Section padding="default">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-6">
            {content.headerTitle}{' '}
            <span className="text-primary-red">{content.headerHighlight}</span>
          </h1>
        </motion.div>

        {/* Our Story + Mission */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-6">
              {content.storyTitle}
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              {content.storyParagraphs.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>

            <div className="mt-8 bg-cream-50 rounded-xl p-6 border-l-4 border-primary-red">
              <h3 className="text-sm font-inter font-semibold uppercase tracking-widest text-primary-red mb-3">
                {content.missionTitle}
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed italic">
                "{content.missionQuote}"
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-xl overflow-hidden shadow-lg aspect-video lg:aspect-square"
          >
            {content.storyMediaType === 'video' ? (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              >
                <source src={storyMediaUrl} type="video/mp4" />
              </video>
            ) : (
              <img
                src={storyMediaUrl}
                alt="About story"
                className="w-full h-full object-cover"
              />
            )}
          </motion.div>
        </div>

        {/* Foundation Stats */}
        <div className="bg-cream-50 rounded-2xl p-8 md:p-12 mb-20">
          <h3 className="text-2xl md:text-3xl font-playfair font-bold text-primary-black mb-8 text-center">
            {content.foundationTitle}
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {content.foundationStats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-primary-red font-playfair font-bold text-4xl mb-2">{stat.value}</div>
                <div className="text-gray-700 font-inter">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
              {content.valuesTitle}
            </h2>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
              {content.valuesDescription}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-sm font-inter font-semibold uppercase tracking-widest text-gray-500 mb-6">
                Core Values
              </h3>
              {content.coreValues.map((value, index) => {
                const IconComponent =
                  iconMap[value.icon as keyof typeof iconMap] || Shield;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-red/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-primary-red" />
                    </div>
                    <div>
                      <h4 className="font-playfair font-bold text-primary-black mb-1">{value.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-inter font-semibold uppercase tracking-widest text-gray-500 mb-6">
                {content.strengthsTitle}
              </h3>
              {content.strengths.map((strength, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                >
                  <div className="w-10 h-10 rounded-full bg-secondary-sage/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-secondary-sage font-playfair font-bold text-lg">{index + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-playfair font-bold text-primary-black mb-1">{strength.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{strength.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Our Supply Process */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
              {content.processTitle}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl">
              {content.processDescription}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {content.processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-cream-50 rounded-xl p-6 shadow-sm"
              >
                <div className="text-4xl font-playfair font-bold text-primary-red/20 mb-3">
                  {step.number}
                </div>
                <h3 className="text-xl font-playfair font-bold text-primary-black mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Strong Supplier Relationships */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-6">
              {content.relationshipsTitle}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {content.relationshipsDescription}
            </p>
            <div className="space-y-3 mb-8">
              {content.network.map((item, index) => {
                const IconComponent =
                  iconMap[item.icon as keyof typeof iconMap] || Package;
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-red/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-4 h-4 text-primary-red" />
                    </div>
                    <span className="text-gray-700 font-inter">{item.label}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-gray-700 leading-relaxed mb-4 font-medium">
              {content.benefitsTitle}
            </p>
            <ul className="space-y-2">
              {content.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-600">
                  <CheckCircle className="w-5 h-5 text-secondary-sage flex-shrink-0 mt-0.5" />
                  {benefit}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-gray-700 italic text-sm leading-relaxed border-l-2 border-secondary-sage pl-4">
              {content.relationshipsNote}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-xl overflow-hidden shadow-lg aspect-video"
          >
            <img
              src={relationshipsImage}
              alt="Supplier relationships"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>

        {/* Quality Control */}
        <div id="quality" className="mb-20">
          <div className="grid md:grid-cols-2 gap-16 items-center">

            {/* Left: image collage — tall portrait + two stacked */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="flex gap-3"
              style={{ height: '520px' }}
            >
              {/* Large portrait — full height on the left */}
                <div className="flex-1 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src={qualityImages[0]}
                    alt="Warehouse sacks"
                    className="w-full h-full object-cover"
                  />
                </div>
              {/* Two equal images stacked on the right */}
              <div className="flex-1 flex flex-col gap-3">
                <div className="flex-1 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src={qualityImages[1]}
                    alt="Machines and sacks"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src={qualityImages[2]}
                    alt="Store sacks"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right: heading + numbered editorial list */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-10"
              >
                <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
                  {content.qualityTitle}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {content.qualityDescription}
                </p>
              </motion.div>

              <div>
                {content.qualityChecks.map((check, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="border-t border-gray-200 py-6 flex gap-6 items-start"
                  >
                    <span className="text-primary-red font-playfair font-bold text-3xl leading-none w-10 flex-shrink-0 select-none">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <p className="text-gray-700 text-base leading-relaxed pt-1">{check}</p>
                  </motion.div>
                ))}
                <div className="border-t border-gray-200" />
              </div>
            </div>

          </div>
        </div>

        {/* Certifications */}
        <CertificationsSection
          title={content.certificationsTitle}
          description={content.certificationsDescription}
          certs={certifications}
        />

        {/* Promise */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-secondary-terracotta/20 to-secondary-sage/20 rounded-2xl p-8 md:p-12 text-center"
        >
          <h3 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-6">
            {content.promiseTitle}
          </h3>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
            "{content.promiseBody}"
          </p>
        </motion.div>
      </Section>
    </main>
  );
};

export default About;
